export type AuditDashboardReceiptWindowRelation = "before_period" | "in_period" | "after_period";

export interface AuditDashboardAuditReceiptItem {
  auditId: string;
  time: string | null;
  receiptAuditQueueId: string | null;
  previousReceiptAuditQueueId: string | null;
  previousAuditTime: string | null;
}

export interface AuditDashboardPosReceiptItem {
  auditId: string | null;
  windowRelation: AuditDashboardReceiptWindowRelation | null;
  receiptAuditQueueId: string;
  vendingSalesId: string | null;
  reportType: string | null;
  locationKey: string | null;
  reportDate: string | null;
  reportTime: string | null;
  status: string | null;
  scratcherCount: number;
  scratcherAmount: number;
  drawGameCount: number;
  drawGameAmount: number;
}

const parseStringOrNull = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }

  const nextValue = String(value).trim();
  return nextValue ? nextValue : null;
};

const parseNumber = (value: unknown): number => {
  if (value == null || value === "") {
    return 0;
  }

  const nextValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(nextValue) ? nextValue : 0;
};

const parseWindowRelation = (value: unknown): AuditDashboardReceiptWindowRelation | null => {
  if (value === "before_period" || value === "in_period" || value === "after_period") {
    return value;
  }

  return null;
};

const mapAuditReceipt = (audit: any): AuditDashboardAuditReceiptItem => ({
  auditId: String(audit?.audit_id ?? audit?.id ?? ""),
  time: parseStringOrNull(audit?.time ?? audit?.report_datetime ?? audit?.audit_date),
  receiptAuditQueueId: parseStringOrNull(audit?.receipt_audit_queue_id),
  previousReceiptAuditQueueId: parseStringOrNull(audit?.previous_receipt_audit_queue_id),
  previousAuditTime: parseStringOrNull(audit?.previous_audit_time),
});

const mapPosReceipt = (receipt: any): AuditDashboardPosReceiptItem | null => {
  const receiptAuditQueueId = parseStringOrNull(receipt?.receipt_audit_queue_id);

  if (!receiptAuditQueueId) {
    return null;
  }

  return {
    auditId: parseStringOrNull(receipt?.audit_id),
    windowRelation: parseWindowRelation(receipt?.window_relation),
    receiptAuditQueueId,
    vendingSalesId: parseStringOrNull(receipt?.vending_sales_id),
    reportType: parseStringOrNull(receipt?.report_type),
    locationKey: parseStringOrNull(receipt?.location_key),
    reportDate: parseStringOrNull(receipt?.report_date),
    reportTime: parseStringOrNull(receipt?.report_time),
    status: parseStringOrNull(receipt?.status),
    scratcherCount: parseNumber(receipt?.scratcher_count),
    scratcherAmount: parseNumber(receipt?.scratcher_amount),
    drawGameCount: parseNumber(receipt?.draw_game_count),
    drawGameAmount: parseNumber(receipt?.draw_game_amount),
  };
};

export default class AuditDashboardReceiptsByDateModel {
  private readonly _audits: AuditDashboardAuditReceiptItem[];
  private readonly _posReceipts: AuditDashboardPosReceiptItem[];

  constructor(audits: AuditDashboardAuditReceiptItem[], posReceipts: AuditDashboardPosReceiptItem[]) {
    this._audits = audits;
    this._posReceipts = posReceipts;
  }

  get audits(): AuditDashboardAuditReceiptItem[] {
    return this._audits;
  }

  get posReceipts(): AuditDashboardPosReceiptItem[] {
    return this._posReceipts;
  }

  getAuditById(auditId: string | null | undefined): AuditDashboardAuditReceiptItem | null {
    if (!auditId) {
      return null;
    }

    return this._audits.find((audit) => audit.auditId === auditId) ?? null;
  }

  getPosReceiptsByAuditId(auditId: string | null | undefined): AuditDashboardPosReceiptItem[] {
    if (!auditId) {
      return [];
    }

    return this._posReceipts.filter((receipt) => receipt.auditId === auditId);
  }

  static fromServerData(data: unknown): AuditDashboardReceiptsByDateModel {
    const audits = Array.isArray((data as any)?.audits) ? (data as any).audits.map(mapAuditReceipt) : [];
    const posReceipts = Array.isArray((data as any)?.pos_receipts)
      ? (data as any).pos_receipts.map(mapPosReceipt).filter(Boolean)
      : [];

    return new AuditDashboardReceiptsByDateModel(
      audits.filter((audit: AuditDashboardAuditReceiptItem) => Boolean(audit.auditId)),
      posReceipts as AuditDashboardPosReceiptItem[]
    );
  }

  static createEmpty(): AuditDashboardReceiptsByDateModel {
    return new AuditDashboardReceiptsByDateModel([], []);
  }
}
